import { useState } from 'react';
import styled from '@emotion/styled';
import GroupIcon from '@mui/icons-material/Group';
import { format, parse } from 'date-fns';
import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { uiTheme as t, Button, DateField, FileDropZone, SegmentedControl, SelectField } from 'Components/ui';
import { PRIORITY_ID } from 'Constants/priority';
import { priority } from 'Constants/Database';
import useTranslation from 'Hooks/useTranslation';
import SelectIcon from '../SelectIcon';
import UserAutocomplete from 'Pages/EditCourseSessions/Components/UserAutocomplete';
import type { BulkApproveFormValues } from './schema';
import useInstructorOptions from './useInstructorOptions';

/** Empleado tal como lo expone UserAutocomplete (lista de empleados). */
interface EmployeeOption {
  id: string;
  fullName: string;
}

interface Props {
  control: Control<BulkApproveFormValues>;
  setValue: UseFormSetValue<BulkApproveFormValues>;
  priorityId: string;
  expanded: boolean;
  toggleExpanded: () => void;
  eligibleCount: number;
}

const priorityIdToLegacyType = (priorityId: string): string => {
  if (priorityId === PRIORITY_ID.normativa) return priority.Normativa;
  if (priorityId === PRIORITY_ID.institucional) return priority.Institucional;
  return priority.Tecnica;
};

const DATE_FORMAT = 'yyyy-MM-dd';
const toInputDate = (date?: Date | null) => (date ? format(date, DATE_FORMAT) : '');
const fromInputDate = (value: string) => (value ? parse(value, DATE_FORMAT, new Date()) : null);

const Column = styled.div({ display: 'flex', flexDirection: 'column', gap: 16 });

const Head = styled.div({ display: 'flex', alignItems: 'center', gap: 12 });

const IconBox = styled.div({
  width: 60,
  height: 60,
  borderRadius: t.radius.sm,
  background: t.color.accentSoft,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
});

const HeadTitle = styled.span({ fontFamily: t.font, fontSize: 18, fontWeight: 600, color: t.color.title });

const Message = styled.p({
  margin: 0,
  fontFamily: t.font,
  fontSize: 14,
  lineHeight: 1.5,
  color: t.color.text,
});

const FieldLabel = styled.span({ fontFamily: t.font, fontSize: 13.5, fontWeight: 600, color: t.color.title });

const InstructorBlock = styled.div({ display: 'flex', flexDirection: 'column', gap: 8 });

const DateRow = styled.div({ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' });

const DateBlock = styled.div({ flex: 1, minWidth: 180 });

const DateRowInner = styled.div({ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' });

const Error = styled.p({ margin: '4px 0 0', fontFamily: t.font, fontSize: 12, color: t.color.danger });

const EvidenceSection = ({ control, setValue, priorityId, expanded, toggleExpanded, eligibleCount }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');

  const isNormativa = priorityId === PRIORITY_ID.normativa;
  const isTecnica = priorityId === PRIORITY_ID.tecnica;
  const isInstitucional = priorityId === PRIORITY_ID.institucional;
  const legacyType = priorityIdToLegacyType(priorityId);

  // El instructor solo aplica cuando hay aprobación manual (todo excepto Normativa).
  const { options: instructorOptions, loading: instructorsLoading } = useInstructorOptions(!isNormativa);

  // Origen del instructor: catálogo (externo) o empleado (interno).
  const instructorType = useWatch({ control, name: 'instructorType' }) ?? 'external';
  // El empleado elegido se guarda local para que UserAutocomplete lo muestre;
  // al form solo viaja su id.
  const [internalEmployee, setInternalEmployee] = useState<EmployeeOption | null>(null);

  const handleInstructorTypeChange = (next: 'external' | 'internal') => {
    if (!next || next === instructorType) return;
    setValue('instructorType', next);
    setValue('instructorId', null);
    setValue('instructorUserId', null);
    setInternalEmployee(null);
  };

  const message = isNormativa
    ? t2('NormativeMessage')
    : isInstitucional
      ? t2('InstitutionalMessage')
      : t2('TechnicalMessage');

  return (
    <Column>
      <Head>
        <IconBox>
          <SelectIcon priorityType={legacyType} />
        </IconBox>
        <HeadTitle>{t2('AssessCourse')}</HeadTitle>
      </Head>

      <Message>{message}</Message>

      {isTecnica && (
        <Controller
          name="file"
          control={control}
          render={({ field, fieldState }) => (
            <div>
              <FileDropZone
                accept=".doc,.docx,.pdf,.mov,.mp4"
                size="compact"
                file={field.value}
                onFile={file => field.onChange(file)}
                onRemove={() => field.onChange(null)}
                labels={{
                  prompt: t2('evidence_drop_prompt') as string,
                  browse: t2('evidence_drop_browse') as string,
                  remove: t2('evidence_remove') as string,
                  hint: t2('TypeFiles') as string,
                }}
              />
              {fieldState.error ? <Error id="evidence-error">{fieldState.error.message}</Error> : null}
            </div>
          )}
        />
      )}

      {!isNormativa && (
        <InstructorBlock>
          <FieldLabel>{t2('instructor_label')}</FieldLabel>
          <SegmentedControl
            options={[
              { value: 'external', label: t2('instructor_type_external') as string },
              { value: 'internal', label: t2('instructor_type_internal') as string },
            ]}
            value={instructorType}
            onChange={handleInstructorTypeChange}
          />

          {instructorType === 'external' ? (
            <Controller
              name="instructorId"
              control={control}
              render={({ field }) => (
                <SelectField
                  id="instructor-id"
                  value={field.value ?? ''}
                  onChange={value => field.onChange(value || null)}
                  placeholder={t2('instructor_placeholder') as string}
                  options={instructorOptions.map(option => ({ value: option.id, label: option.name }))}
                  disabled={instructorsLoading}
                />
              )}
            />
          ) : (
            <Controller
              name="instructorUserId"
              control={control}
              render={({ field }) => (
                <UserAutocomplete
                  value={internalEmployee}
                  onChange={(employee: EmployeeOption | null) => {
                    setInternalEmployee(employee);
                    field.onChange(employee?.id ?? null);
                  }}
                  placeholder={t2('instructor_internal_placeholder')}
                  noOptionsText={t2('instructor_internal_empty')}
                />
              )}
            />
          )}
        </InstructorBlock>
      )}

      {!isNormativa && (
        <DateRow>
          <DateBlock>
            <FieldLabel>{t2('evaluation_date')}</FieldLabel>
            <DateRowInner>
              <Controller
                name="evaluationDate"
                control={control}
                render={({ field }) => (
                  <DateField
                    id="evaluation-date"
                    value={toInputDate(field.value as Date | null)}
                    onChange={date => {
                      const parsed = fromInputDate(date ?? '');
                      field.onChange(parsed);
                      setValue('evaluationDateTouched', true);
                    }}
                  />
                )}
              />
              <Button type="button" variant="secondary" onClick={toggleExpanded} disabled={eligibleCount === 0}>
                <GroupIcon />
                {expanded ? t2('hide_multi_select') : `${t2('select_multiple_employees')} (${eligibleCount})`}
              </Button>
            </DateRowInner>
          </DateBlock>
        </DateRow>
      )}
    </Column>
  );
};

export default EvidenceSection;
